import { useEffect, useRef, useState } from 'react';
import { ComponentRendering, Field, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import { icon_swipe_left } from '@gn-audio/icons';
import {
  ComponentProps,
  InteractionHint,
  resolveBackgroundStyle,
  useBreakpoint,
  useFallbackTranslation,
} from '@gn-audio/jss-components';

import HeaderCell from './HeaderCell';
import TableRow from './TableRow';
import { Column, RowFields } from './tableSpotTypes';

type TableSpotFields = {
  header: Field<string>;
  background: Field<string>;
  columns: Column[];
  rows: RowFields[];
  minWidth: Field<string>;
  maxWidth: Field<string>;
};

type TableSpotProps = {
  fields: TableSpotFields;
  sanitizedColumns: Column[];
  sanitizedRows: RowFields[];
} & ComponentProps;

const TableSpot = ({ fields, sanitizedColumns, sanitizedRows }: TableSpotProps) => {
  const { t } = useFallbackTranslation();
  const { isBelowMd } = useBreakpoint('md', true);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const { header, background, minWidth, maxWidth } = fields ?? {};

  const spotBackgroundStyle = resolveBackgroundStyle(background.value as string);
  const columnColors = sanitizedColumns?.map(({ backgroundOddCells, backgroundEvenCells }) => ({
    backgroundOddCells: backgroundOddCells?.value,
    backgroundEvenCells: backgroundEvenCells?.value,
  }));
  const tableRef = useRef<HTMLTableElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkHorizontalScroll = () => {
      const isActive =
        isBelowMd &&
        (containerRef.current?.scrollWidth || 0) > (containerRef.current?.clientWidth || 0);
      setIsOverflowing(isActive);
    };

    checkHorizontalScroll();

    window.addEventListener('resize', checkHorizontalScroll);

    return () => {
      window.removeEventListener('resize', checkHorizontalScroll);
    };
  }, [isBelowMd]);

  return (
    <div className="container-fluid !py-0" style={spotBackgroundStyle}>
      <section className="container">
        {!!header?.value && <h2 className="heading-2 text-center">{header.value}</h2>}
        {isOverflowing && (
          <div className={classNames('mb-8', { 'mr-4 xs:mr-8': isOverflowing })}>
            <InteractionHint icon={icon_swipe_left} text={t('system.MobileSwipeHint')} />
          </div>
        )}
        <div className="relative w-full overflow-hidden">
          <div
            className={classNames('overflow-x-auto', { 'pr-4 xs:pr-8': isOverflowing })}
            ref={containerRef}
          >
            <table
              className={'mx-auto h-fit w-full border-collapse border-hidden'}
              ref={tableRef}
              style={{ minWidth: minWidth?.value || '100%', maxWidth: maxWidth?.value || '100%' }}
            >
              <thead>
                <tr>
                  {sanitizedColumns?.map((column) => (
                    <HeaderCell key={column.id} column={column} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sanitizedRows?.map((row, index) => (
                  <TableRow
                    key={row.id}
                    row={row}
                    isRowOdd={(index + 1) % 2 > 0}
                    colors={columnColors}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export const getStaticProps = async ({ fields }: ComponentRendering<TableSpotFields>) => {
  const { columns, rows } = fields || {};

  const sanitizedColumns = columns?.map((column) => ({
    ...column,
    title: column.title
      ? { ...column.title, value: sanitize(column.title.value || '') }
      : column.title,
  }));

  const sanitizedRows = rows?.map((row) => ({
    ...row,
    cells: row.cells?.map((cell) => {
      if ('icon' in cell) return cell;

      return {
        ...cell,
        content: cell.content
          ? { ...cell.content, value: sanitize(cell.content.value || '') }
          : cell.content,
      };
    }),
  }));

  return {
    sanitizedColumns: sanitizedColumns || [],
    sanitizedRows: sanitizedRows || [],
  };
};

export default withDatasourceCheck()(TableSpot);
